import {useState} from "react"
import {format} from "date-fns"
import type {OtkDocumentDetail} from "shared"

import {Dialog, DialogContent, DialogDescription, DialogTitle,} from "@/components/ui/dialog"
import {ConsentDownload} from "@/components/document-editor/ConsentDownload"
import {DocumentCard} from "@/components/document-editor/DocumentCard"
import {DocumentPreviewDialog} from "@/components/document-editor/DocumentPreviewDialog"
import {DocumentWizardNav} from "@/components/document-editor/DocumentWizardNav"
import {DocumentEditAction} from "@/components/documents/DocumentEditAction"
import {HeaderDeleteDocumentDialog} from "@/components/documents/HeaderDeleteDocumentDialog"
import {getOtkDocumentFilesWithUrl, retryOtkDocument} from "@/lib/api/documents"
import {pendingDocumentFromError} from "@/lib/documentEditor"
import {
  buildSignedOtkDocumentPayloadFromDetail,
  documentCardSections,
  documentSignReadiness,
  printDataFromDetail,
} from "@/lib/documentPreview"
import {DocumentEdit} from "@/pages/DocumentEdit"
import {useReadOnly, useSession} from "@/store/session"

/**
 * documents.md's `DocumentModal` — `DOCUMENT_VIEW_PRESENTATION = "modal"`.
 * Implements the documented modal chrome (viewport-sized frame, fixed
 * header with name/delete/edit-toggle/autosave text, independently
 * scrolling body, no outside-pointer-down dismissal, autofocus suppressed).
 *
 * Deliberately decoupled from `DocumentRow` (the summary shape the
 * `/documents` list itself fetches): `mode="view"` is opened from that
 * local list, but `mode="edit"` is opened at `/documents/edit/:id`, whose
 * own route component fetches the fuller `OtkDocumentDetail` via
 * `getOtkDocumentById` — a different shape with a different (English)
 * status enum. Callers compute `documentNumber`/`canDelete` themselves from
 * whichever shape they actually have, so this component never needs to
 * know which one it was.
 */
export function DocumentModal({
  canDelete,
  detail,
  documentId,
  documentNumber,
  mode,
  onClose,
  onDelete,
  onSigned,
  version,
}: {
  /** Whether the header's delete action renders at all — the caller's own `status === draft` check. */
  canDelete: boolean
  /** Fetched document body, threaded into `DocumentEdit` to seed its tab state. */
  detail?: OtkDocumentDetail | null
  /** Draft id, threaded into `DocumentEdit` so its vehicle/owner autosave can `PATCH /otk/documents/:id` — also `DocumentEditAction`'s own target. */
  documentId?: string
  documentNumber?: string
  mode: "view" | "edit"
  onClose: () => void
  onDelete: () => void | Promise<void>
  /** Bubbles the updated document up to whichever route owns `detail` (`DocumentEditModalRoute`/`DocumentModalRoute`'s own `setDetail`), once `DocumentPreviewDialog`'s sign flow succeeds. */
  onSigned?: (document: OtkDocumentDetail) => void
  /** Draft's current `version`, for the autosave patch's `expectedVersion`. */
  version?: number
}) {
  const readOnly = useReadOnly()
  const documentName = documentNumber
    ? `Документ ${documentNumber}`
    : "Документ ОТК"
  // document-editor.md's DE-delete: `HeaderDeleteDocumentDialog` is hidden
  // entirely in read-only mode (not shown-disabled, the `documents.md` row
  // action's own convention) — the caller's own `canDelete` is just the
  // status check, so read-only is folded in here.
  const canDeleteDocument = canDelete && !readOnly

  // document-editor.md's `autosaveStatus`, portaled into this chrome header's
  // aside slot: `DocumentEdit`'s own `lastSavedAt` reported up via
  // `onAutosaveStatusChange`, since the header that renders it lives here,
  // one level above `DocumentEdit`'s own body. `null` until `DocumentEdit`'s
  // first report arrives (view mode never reports at all, since it has no
  // autosave), in which case the header aside stays silent instead of
  // claiming a save that hasn't happened.
  const [autosaveStatus, setAutosaveStatus] = useState<
    { savedAt: Date | null } | undefined
  >(undefined)

  async function handleDelete() {
    await onDelete()
    onClose()
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      open
    >
      <DialogContent
        className="flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-[1920px] flex-col gap-0 p-0 sm:max-w-[1920px] [scrollbar-gutter:stable]"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogDescription className="sr-only">
          Перегляд і редагування документа ОТК
        </DialogDescription>
        <div className="flex shrink-0 items-center gap-3 border-b p-4 pr-10">
          <DialogTitle className="text-base font-medium">
            {documentName}
          </DialogTitle>
          {canDeleteDocument && (
            <HeaderDeleteDocumentDialog
              documentName={documentName}
              onDelete={handleDelete}
            />
          )}
          {canDelete && documentId && (
            <DocumentEditAction documentId={documentId} mode={mode} />
          )}
          {autosaveStatus && (
            <span className="ml-auto text-xs text-muted-foreground">
              {autosaveStatus.savedAt === null
                ? "Збереження..."
                : `Автозбережено о ${format(autosaveStatus.savedAt, "HH:mm")}`}
            </span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 pr-2">
          {mode === "edit" ? (
            <DocumentEdit
              detail={detail}
              documentId={documentId}
              documentNumber={documentNumber}
              embedded
              onAutosaveStatusChange={(savedAt) => setAutosaveStatus({ savedAt })}
              onSigned={onSigned}
              version={version}
            />
          ) : detail ? (
            <ViewModeBody detail={detail} onSigned={onSigned} />
          ) : (
            <p className="text-sm text-muted-foreground">
              Завантаження документа...
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * View mode's counterpart to `DocumentEdit`'s `Tabs` + `DocumentWizardNav`
 * row (`pages/DocumentEdit.tsx` around its own `ConsentDownload`/
 * `DocumentPreviewDialog` action dock): the same left-rail actions, reusing
 * `DocumentWizardNav` with an empty `sections` list so it renders only its
 * sticky action dock, no step list — there's no wizard to navigate in
 * read-only view mode. `documentSignReadiness` mirrors `DocumentEdit.tsx`'s
 * own wizard-completeness predicate, evaluated against the fetched `detail`
 * snapshot instead of live form state. Read-only itself is not folded into
 * `disabledReasons` here — `DocumentPreviewDialog` computes that (and its
 * own reason string) internally via its own `useReadOnly()` call, same as
 * `DocumentEdit.tsx`.
 */
function ViewModeBody({
  detail,
  onSigned,
}: {
  detail: OtkDocumentDetail
  onSigned?: (document: OtkDocumentDetail) => void
}) {
  const subject = useSession((s) => s.subject)
  const readiness = documentSignReadiness(detail)
  const sections = documentCardSections(detail)
  // Preview/sign/consent are draft-only actions (`DocumentEdit.tsx`'s own
  // `isDraftDocument` gate) — a document past ЧЕРНЕТКА reached via `/documents/:id`
  // (`DocumentModalRoute`'s `mode="view"`, always mounted regardless of status)
  // must not still offer them. The one exception is `SUBMISSION_PENDING`:
  // signed but not yet accepted by ЄДРТЗ, it keeps the dialog for its
  // «Повторити надсилання» panel.
  const isDraftDocument = detail.status === "DRAFT"
  const isSubmissionPending = detail.status === "SUBMISSION_PENDING"

  // View mode's `buildSignPayload` — the stored `detail` snapshot, not live
  // wizard state (there's no wizard here), same source `documentCardSections`
  // above already reads from. Photo hashes are fetched fresh
  // right before signing, same reasoning as `DocumentEdit.tsx`'s own
  // `buildSignPayload`.
  async function buildSignPayload(selection: {
    electronicSeries: string
    electronicNumber: string
    blankSeries?: string
    blankNumber?: string
  }) {
    const filesResponse = await getOtkDocumentFilesWithUrl(detail.id)
    return buildSignedOtkDocumentPayloadFromDetail(
      detail,
      filesResponse.files,
      selection
    )
  }

  async function handleRetry() {
    try {
      const document = await retryOtkDocument(detail.id)
      onSigned?.(document)
    } catch (error) {
      // A refused or failed attempt still returns the document with its new
      // `nextRetryAt` — apply it before surfacing the error.
      const pending = pendingDocumentFromError(error)
      if (pending) onSigned?.(pending)
      throw error
    }
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <DocumentWizardNav
        activeValue=""
        actions={
          (isDraftDocument || isSubmissionPending) && (
            <>
              {isDraftDocument && <ConsentDownload />}
              <DocumentPreviewDialog
                buildSignPayload={buildSignPayload}
                disabled={readiness.disabledReasons.length > 0}
                disabledReasons={readiness.disabledReasons}
                documentId={detail.id}
                isNonconformityAct={readiness.isNonconformityAct}
                onSigned={onSigned}
                printData={printDataFromDetail(detail, subject)}
                submissionPending={isSubmissionPending}
                submissionConfirmed={detail.status === "SENT"}
                retryAt={detail.submission?.nextRetryAt ? Date.parse(detail.submission.nextRetryAt) : undefined}
                onRetry={handleRetry}
                punkt={detail.punktId}
                sections={sections}
                triggerLabel="Перегляд, Підпис та Друк"
                triggerVariant="default"
              />
            </>
          )
        }
        onValueChange={() => undefined}
        sections={[]}
      />
      <div className="min-w-0 flex-1">
        <DocumentCard sections={sections} />
      </div>
    </div>
  )
}
